import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ButtonComp from "../../../../components/Button";
import { useCaseCreationStore } from "../../../../store/useCaseCreationStore";
import { formatR } from "../../../../utils/money";

// Mirrors the backend: "VAT15" -> 0.15; non-VAT / blank -> 0.
const vatRate = (taxCode?: string): number => {
  if (!taxCode) return 0;
  const digits = taxCode.replace(/[^0-9]/g, "");
  return digits ? parseInt(digits, 10) / 100 : 0;
};

export default function PackagePricing({ onBack, onNext }: any) {
  const { packageSelection } = useCaseCreationStore();
  const rows = packageSelection.lineItems ?? [];

  const priced = rows.map((r) => {
    const lineSubtotal = r.unitPrice * r.quantity;
    const lineVat = lineSubtotal * vatRate(r.taxCode);
    return { ...r, lineSubtotal, lineVat, lineTotal: lineSubtotal + lineVat };
  });

  const subtotal = priced.reduce((s, r) => s + r.lineSubtotal, 0);
  const vatTotal = priced.reduce((s, r) => s + r.lineVat, 0);
  const grandTotal = subtotal + vatTotal;

  return (
    <Paper elevation={0} sx={{ p: 4, mt: 3, borderRadius: 3, backgroundColor: "#fff" }}>
      <Typography fontSize={18} fontWeight={800} color="#111827" mb={2}>
        Package / Pricing
      </Typography>

      {priced.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed #E5E7EB",
            borderRadius: 2,
            py: 6,
            textAlign: "center",
          }}
        >
          <Typography fontSize={14} color="#6B7280" fontWeight={600}>
            No services selected yet. Go back to Requested Services to build the quote.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: "#374151" }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 70 }}>Qty</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 130 }}>Unit Price</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 130 }}>Subtotal</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 120 }}>VAT</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#374151", width: 130 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priced.map((r, index) => (
                <TableRow key={`${r.code}-${index}`} sx={{ "& td": { py: 1.75 } }}>
                  <TableCell sx={{ color: "#111827", fontWeight: 700 }}>{r.name}</TableCell>
                  <TableCell sx={{ color: "#4B5563" }}>{r.quantity}</TableCell>
                  <TableCell sx={{ color: "#4B5563" }}>{formatR(r.unitPrice)}</TableCell>
                  <TableCell sx={{ color: "#4B5563" }}>{formatR(r.lineSubtotal)}</TableCell>
                  <TableCell sx={{ color: "#4B5563" }}>{formatR(r.lineVat)}</TableCell>
                  <TableCell sx={{ color: "#111827", fontWeight: 800 }}>{formatR(r.lineTotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {priced.length > 0 ? (
        <Stack spacing={0.5} sx={{ mt: 2, ml: "auto", width: { xs: "100%", sm: 320 } }}>
          <Row label="Subtotal (excl. VAT)" value={formatR(subtotal)} />
          <Row label="VAT" value={formatR(vatTotal)} />
          <Box sx={{ borderTop: "1px solid #E5E7EB", my: 0.5 }} />
          <Row label="Total (incl. VAT)" value={formatR(grandTotal)} strong />
        </Stack>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 6 }}>
        <ButtonComp variant="outlined" onClick={onBack} height={50} width={190}>
          Back
        </ButtonComp>
        <ButtonComp variant="contained" onClick={onNext} height={50} width={190}>
          Next
        </ButtonComp>
      </Box>
    </Paper>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography fontSize={14} fontWeight={strong ? 800 : 600} color={strong ? "#111827" : "#6B7280"}>
        {label}
      </Typography>
      <Typography fontSize={14} fontWeight={strong ? 900 : 700} color="#111827">
        {value}
      </Typography>
    </Box>
  );
}
